import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { SectionMetadata } from 'app/providers/storage-api/collection.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SectionService } from 'app/services/section.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { WidgetDataSource } from '../../../../models';
import { Widget } from '../../../../providers/bridge-api/dashboard.service';
import { AnalyticItemTypeMap } from '../../../dashboard/services/analytic-item-types';
import {
  AnalyticItemsDialogOptions,
  AnalyticItemSelectorComponent,
} from '../analytic-item-selector/analytic-item-selector.component';
import { SelectedSection } from '../section-selector/section-selector.component';
import { CrosstabWidgetOptions } from '../widget-crosstab/widget-crosstab.component';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';

export interface WidgetAnalyticItemDialogResult {
  analyticItem: string;
  subtypes: string;
  customField: boolean;
  standardField: boolean;
  sectionProperty: boolean;
  showBlanksChanged: boolean;
  selectedSection: SelectedSection;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-analytic-item-dialog',
  templateUrl: './widget-analytic-item-dialog.component.html',
  styleUrls: ['./widget-analytic-item-dialog.component.scss'],
  standalone: false,
})
export class WidgetAnalyticItemDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('analyticItemSelector') analyticItemSelector: AnalyticItemSelectorComponent;
  @Input() options: AnalyticItemsDialogOptions;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any>();
  @Output() isSectionSelectorAvailableChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  widget: Widget<CrosstabWidgetOptions>;
  widgetDataSource: WidgetDataSource;
  report: AbstractChartReport;
  analyticItemTypes: AnalyticItemTypeMap;

  selectedAnalyticItem: string;
  selectedSubtypes: string[] = [];

  isLoading = false;
  isSectionSelectorAvailable = false;
  sectionMetadata: SectionMetadata[] = [];
  selectedSection: SelectedSection[];

  subtypeSelectionText: string;
  sectionSelectionText: string;
  saveButtonTooltip: { text: string };
  isValid = true;

  constructor(
    private sectionService: SectionService,
    private logger: LoggerService
  ) {}

  async ngOnInit() {
    this.widget = this.options.widget;
    this.widgetDataSource = this.options.widgetDataSource;
    this.report = this.options.report;
    this.analyticItemTypes = this.options.analyticItemTypes;

    if (this.widgetDataSource.hasSections) {
      await this.loadSectionMetadata();

      this.setSectionSelectorAvailable();
    }
  }

  checkValidity(): boolean {
    if (
      this.isSectionSelectorAvailable &&
      this.selectedSection &&
      (this.selectedSection.some((sec) => !sec.field) ||
        this.selectedSection.some((sec) => !sec.buckets.length))
    ) {
      return false;
    }

    if (!this.isAutoTopicExcludedOrEveryBucketSelected()) {
      return false;
    }

    if (this.analyticItemSelector && !this.analyticItemSelector.hasSelection) {
      return false;
    }

    return true;
  }

  assembleSaveButtonTooltip() {
    if (this.isValid) {
      return;
    }

    if (this.sectionSelectionText) {
      return { text: this.sectionSelectionText };
    }

    if (!this.isAutoTopicExcludedOrEveryBucketSelected()) {
      return { text: translate('widgets.analytic-item-selector.must-exclude-auto-topics') };
    }

    return { text: this.subtypeSelectionText };
  }

  onAnalyticItemChange(obj: { item: string; tooltip: string }) {
    this.selectedAnalyticItem = obj.item;
    this.subtypeSelectionText = obj.tooltip;

    setTimeout(() => {
      this.setSectionSelectorAvailable();

      this.validate();
    });
  }

  onSubtypesChange(subtypes: string[]): void {
    this.selectedSubtypes = subtypes;

    this.validate();
  }

  private isAutoTopicExcludedOrEveryBucketSelected(): boolean {
    if (this.selectedAnalyticItem !== 'topics') {
      return true;
    }

    if (!this.widgetDataSource.topicTypes?.includes('auto')) {
      return true;
    }

    if (!this.isSectionSelectorAvailable) {
      return true;
    }

    if (!this.selectedSubtypes.includes('auto')) {
      return true;
    }

    // it means every bucket is selected
    if (!this.selectedSection) {
      return true;
    }

    return false;
  }

  private validate(): void {
    this.isValid = this.checkValidity();
    this.saveButtonTooltip = this.assembleSaveButtonTooltip();
  }

  onSectionSelected(selectedSection: SelectedSection[]): void {
    this.selectedSection = selectedSection;
    this.sectionSelectionText = undefined;

    if (!SectionService.isSelectionComplete(this.selectedSection)) {
      this.sectionSelectionText = translate('widgets.analytic-item-selector.must-select-section');
    }

    this.validate();
  }

  private async loadSectionMetadata(): Promise<void> {
    this.isLoading = true;

    try {
      const allMetadata = await this.sectionService.loadAllSectionMetadata(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      this.sectionMetadata.push(...allMetadata);

      // Add section properties with cardinality > 0 to sectionProperties
      const cardinalityListResult = await this.sectionService.getSectionPropertyCardinality(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      //remove section time from being a selectable item.
      const validSectionProperties = cardinalityListResult.items
        .filter((item) => item.cardinality > 0 && !item.fieldName.includes('sectionTime'))
        .map((item) => item.fieldName);

      // Clear and repopulate to avoid duplicates when dialog reopens
      if (!this.analyticItemTypes.sectionProperties.subTypes) {
        this.analyticItemTypes.sectionProperties.subTypes = [];
      }
      this.analyticItemTypes.sectionProperties.subTypes = [...validSectionProperties];
    } catch (error) {
      this.logger.error(translate('widgets.analytic-item-selector.failed-to-load-sections'), {
        error,
      });
    }

    this.isLoading = false;
  }

  private setSectionSelectorAvailable(): void {
    this.isSectionSelectorAvailable = FilterUtils.isSectionSelectorAvailable(
      this.selectedAnalyticItem
    );

    if (!this.isSectionSelectorAvailable) {
      this.selectedSection = undefined;
    }
    this.isSectionSelectorAvailableChange.emit(this.isSectionSelectorAvailable);
  }

  cancel() {
    this.modalClosed.emit(null);
    if (this.options.resolveFn) {
      this.options.resolveFn(null);
    }
  }

  save() {
    let showBlanks: boolean | undefined;
    if (
      this.analyticItemSelector.customFieldSelected &&
      this.analyticItemSelector.isBlankEnabled &&
      this.analyticItemSelector.isBlankSelected
    ) {
      showBlanks = true;
    }
    const result = {
      analyticItem: this.selectedAnalyticItem,
      subtypes: this.selectedSubtypes.sort().join(),
      customField: this.analyticItemSelector.customFieldSelected,
      standardField: this.analyticItemSelector.standardFieldSelected,
      sectionProperty: this.analyticItemSelector.sectionPropertySelected,
      showBlanksChanged: showBlanks !== this.widget.payload.showBlanks,
      selectedSection: this.selectedSection,
    };

    this.modalClosed.emit(result);
    if (this.options.resolveFn) {
      this.options.resolveFn(result);
    }
  }
}
